"use client";

import { useEffect, useState } from "react";
import { updateEnergy, type Settings } from "@/lib/settings-api";

interface Props {
  settings: Settings | null;
  onUpdate: (s: Settings) => void;
}

const CHRONOTYPES = [
  { value: "early_bird", label: "Early bird (morning focus)" },
  { value: "midday", label: "Midday (late morning focus)" },
  { value: "night_owl", label: "Night owl (evening focus)" },
  { value: "varies", label: "It varies (let DecisionEase learn)" },
] as const;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

function formatHourLabel(hour: number): string {
  if (hour === 0) return "12 AM (midnight)";
  if (hour === 12) return "12 PM (noon)";
  if (hour === 24) return "12 AM next day";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

// Explicit energy-zone setting. Whatever the user picks here outranks the
// learned 30-day pattern AND the population default in BOTH schedule
// engines (backend/services/energy_zones.py) — deep work moves into this
// window on the next plan. "" (no chronotype) + no peak window = let
// learning/defaults decide.
export default function EnergySection({ settings, onUpdate }: Props) {
  const [chronotype, setChronotype] = useState<string>(settings?.chronotype ?? "");
  const [usePeakWindow, setUsePeakWindow] = useState<boolean>(
    settings?.peak_start_hour != null && settings?.peak_end_hour != null,
  );
  const [peakStart, setPeakStart] = useState<number>(settings?.peak_start_hour ?? 19);
  const [peakEnd, setPeakEnd] = useState<number>(settings?.peak_end_hour ?? 22);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const settingsReady = Boolean(settings);

  useEffect(() => {
    if (hasLocalEdits) return;
    setChronotype(settings?.chronotype ?? "");
    const hasWindow =
      settings?.peak_start_hour != null && settings?.peak_end_hour != null;
    setUsePeakWindow(hasWindow);
    setPeakStart(settings?.peak_start_hour ?? 19);
    setPeakEnd(settings?.peak_end_hour ?? 22);
  }, [
    hasLocalEdits,
    settings?.chronotype,
    settings?.peak_start_hour,
    settings?.peak_end_hour,
  ]);

  const isInverted = usePeakWindow && peakEnd <= peakStart;
  const canSave = settingsReady && !saving && !isInverted;

  const handleSave = async () => {
    if (!canSave) return;
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updateEnergy({
        chronotype: chronotype || null,
        peak_start_hour: usePeakWindow ? peakStart : null,
        peak_end_hour: usePeakWindow ? peakEnd : null,
      });
      setHasLocalEdits(false);
      onUpdate(updated);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save energy setting yet.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border shadow-sm p-4"
      data-testid="settings-energy-section"
    >
      <h2 className="text-sm font-semibold text-text-light mb-1">Energy zone</h2>
      <p className="text-[11px] leading-relaxed text-muted mb-3">
        When do you focus best? Deep work moves into this window on the next
        plan. An exact window wins over the rhythm pick, and both win over
        what DecisionEase has learned from your history.
      </p>

      <select
        data-testid="settings-energy-chronotype"
        disabled={!settingsReady}
        value={chronotype}
        onChange={(e) => {
          setHasLocalEdits(true);
          setChronotype(e.target.value);
        }}
        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-xs text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
      >
        <option value="">Not set</option>
        {CHRONOTYPES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      <label className="mt-3 flex items-center gap-2 text-xs text-text">
        <input
          type="checkbox"
          data-testid="settings-energy-use-window"
          disabled={!settingsReady}
          checked={usePeakWindow}
          onChange={(e) => {
            setHasLocalEdits(true);
            setUsePeakWindow(e.target.checked);
          }}
          className="h-4 w-4 rounded border-border accent-[var(--forestMed,#3E5E3A)]"
        />
        Set an exact peak window
      </label>

      {usePeakWindow && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            data-testid="settings-energy-peak-start"
            disabled={!settingsReady}
            value={peakStart}
            onChange={(e) => {
              setHasLocalEdits(true);
              setPeakStart(Number(e.target.value));
            }}
            className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-border bg-card text-xs text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
          >
            {HOUR_OPTIONS.map((hour) => (
              <option key={`peak-start-${hour}`} value={hour}>
                {formatHourLabel(hour)}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted">to</span>
          <select
            data-testid="settings-energy-peak-end"
            disabled={!settingsReady}
            value={peakEnd}
            onChange={(e) => {
              setHasLocalEdits(true);
              setPeakEnd(Number(e.target.value));
            }}
            className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-border bg-card text-xs text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
          >
            {HOUR_OPTIONS.filter((h) => h > 0).map((hour) => (
              <option key={`peak-end-${hour}`} value={hour}>
                {formatHourLabel(hour)}
              </option>
            ))}
            <option value={24}>{formatHourLabel(24)}</option>
          </select>
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          data-testid="settings-energy-save"
          disabled={!canSave}
          onClick={handleSave}
          className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
            canSave
              ? "text-forest bg-forest/10 hover:bg-forest/20"
              : "cursor-not-allowed bg-secondary text-muted"
          }`}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {isInverted && (
        <p className="mt-2 text-xs text-earth">End must be later than start.</p>
      )}
      {saveError && <p className="mt-2 text-xs text-earth">{saveError}</p>}
    </div>
  );
}
